import React, { useState } from 'react';
import { Phone, Search, CheckCircle, AlertCircle, X, Save, Clock } from 'lucide-react';
import PageIdentifier from '../shared/PageIdentifier';
import SCREEN_IDS from '../../utils/screenIds';
import { todayCentral } from '../../shared/utils/dateUtils';

const SchemePaymentEntry = () => {
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Search
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Selected customer and their enrollments
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [enrollmentsLoading, setEnrollmentsLoading] = useState(false);

  // Selected enrollment and pending payments
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);

  // Payment entry form
  const [paymentForm, setPaymentForm] = useState({
    enrollment_id: '',
    payment_month_index: '',
    amount_paid: '',
    payment_method: 'cash',
    notes: '',
    payment_date: todayCentral()
  });

  const [selectedPayments, setSelectedPayments] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Search customers
  const searchCustomers = async (term) => {
    if (term.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      setSearching(true);
      const response = await fetch(`/api/customers/search?term=${encodeURIComponent(term)}&limit=10`);
      const result = await response.json();
      
      if (result.success) {
        setSearchResults(result.data);
      }
    } catch (err) {
      setError('Error searching customers: ' + err.message);
    } finally {
      setSearching(false);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    searchCustomers(value);
  };

  const selectCustomer = async (customer) => {
    setSelectedCustomer(customer);
    setSearchTerm('');
    setSearchResults([]);

    // Fetch their active enrollments
    await fetchEnrollments(customer.id);
  };

  const fetchEnrollments = async (customerId) => {
    try {
      setEnrollmentsLoading(true);
      const response = await fetch(`/api/schemes/enrollments/customer/${customerId}`);
      const result = await response.json();
      
      if (result.success) {
        const activeEnrollments = result.data.filter(
          e => e.status === 'active'
        );
        setEnrollments(activeEnrollments);
      } else {
        setError('Failed to fetch enrollments');
      }
    } catch (err) {
      setError('Error fetching enrollments: ' + err.message);
    } finally {
      setEnrollmentsLoading(false);
    }
  };

  const selectEnrollment = async (enrollment) => {
    setSelectedEnrollment(enrollment);
    
    // Fetch pending payments for this enrollment
    await fetchPendingPayments(enrollment.id);
  };

  const fetchPendingPayments = async (enrollmentId) => {
    try {
      setPaymentsLoading(true);
      // Changed: Use the correct endpoint that filters by enrollment
      const response = await fetch(`/api/schemes/payments/enrollment/${enrollmentId}`);
      const result = await response.json();
      
      if (result.success) {
        // Filter only unpaid payments
        const unpaidPayments = (result.data || []).filter(p => p.is_paid === false);
        setPendingPayments(unpaidPayments);
      }
    } catch (err) {
      setError('Error fetching pending payments: ' + err.message);
    } finally {
      setPaymentsLoading(false);
    }
  };

  const togglePaymentSelection = (paymentIndex) => {
    setSelectedPayments(prev => {
      if (prev.includes(paymentIndex)) {
        return prev.filter(idx => idx !== paymentIndex);
      } else {
        return [...prev, paymentIndex];
      }
    });
  };

  const handlePaymentFormChange = (e) => {
    const { name, value } = e.target;
    setPaymentForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const submitPayment = async (e) => {
    e.preventDefault();

    if (!selectedEnrollment) {
      setError('Please select an enrollment');
      return;
    }

    if (selectedPayments.length === 0) {
      setError('Please select at least one payment to record');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Submit each selected payment
      const results = [];
      for (const paymentIndex of selectedPayments) {
        const payment = pendingPayments[paymentIndex];
        
        const paymentData = {
          paid_amount: parseFloat(paymentForm.amount_paid) || parseFloat(payment.payment_amount || 0),
          payment_method: paymentForm.payment_method,
          notes: paymentForm.notes,
          payment_date: paymentForm.payment_date
        };

        // Use correct endpoint: /api/schemes/payments/:paymentId/record
        const response = await fetch(`/api/schemes/payments/${payment.id}/record`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(paymentData)
        });

        const result = await response.json();
        results.push({ payment, success: result.success, error: result.error });
      }

      // Check results
      const failedPayments = results.filter(r => !r.success);
      if (failedPayments.length === 0) {
        setSuccess(`${selectedPayments.length} payment(s) recorded successfully!`);
        
        // Reset form
        setPaymentForm({
          enrollment_id: '',
          payment_month_index: '',
          amount_paid: '',
          payment_method: 'cash',
          notes: '',
          payment_date: todayCentral()
        });
        setSelectedPayments([]);
        
        // Refresh pending payments
        if (selectedEnrollment) {
          await fetchPendingPayments(selectedEnrollment.id);
        }
        
        setTimeout(() => setSuccess(null), 5000);
      } else {
        setError(`${failedPayments.length} payment(s) failed to record`);
      }
    } catch (err) {
      setError('Error recording payments: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <PageIdentifier pageId={SCREEN_IDS.SCHEMES.PAYMENT_ENTRY} pageName="Record Payments" />
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Record Scheme Payments</h1>
        <p className="text-gray-500 mt-1">Search customers and record gold scheme payments</p>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
          <AlertCircle size={20} />
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-auto">
            <X size={16} />
          </button>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
          <CheckCircle size={20} />
          <span>{success}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: Search & Selection */}
        <div className="lg:col-span-1">
          {/* Customer Search */}
          <div className="bg-white rounded-lg shadow-lg p-4 border border-amber-100 mb-6">
            <h2 className="font-bold text-gray-800 mb-4 text-lg">1. Find Customer</h2>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search by phone or name
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder="Search customer..."
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>
            </div>

            {searching && <p className="text-gray-600 text-sm">Searching...</p>}

            {searchResults.length > 0 && (
              <div className="space-y-2">
                {searchResults.map(customer => (
                  <button
                    key={customer.id}
                    onClick={() => selectCustomer(customer)}
                    className={`w-full text-left p-3 rounded-lg border-2 transition ${
                      selectedCustomer?.id === customer.id
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-gray-200 bg-white hover:border-amber-300'
                    }`}
                  >
                    <p className="font-semibold text-gray-800 text-sm">{customer.name}</p>
                    <p className="text-xs text-gray-500">{customer.phone}</p>
                  </button>
                ))}
              </div>
            )}

            {selectedCustomer && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-xs text-green-600 font-semibold">✓ Customer Selected</p>
                <p className="text-sm font-semibold text-gray-800">{selectedCustomer.name}</p>
              </div>
            )}
          </div>

          {/* Enrollments */}
          {selectedCustomer && (
            <div className="bg-white rounded-lg shadow-lg p-4 border border-amber-100">
              <h2 className="font-bold text-gray-800 mb-4 text-lg">2. Select Enrollment</h2>
              
              {enrollmentsLoading ? (
                <p className="text-gray-600 text-sm">Loading enrollments...</p>
              ) : enrollments.length === 0 ? (
                <p className="text-gray-500 text-sm">No active enrollments for this customer</p>
              ) : (
                <div className="space-y-2">
                  {enrollments.map((enrollment, idx) => (
                    <button
                      key={enrollment.id}
                      onClick={() => selectEnrollment(enrollment)}
                      className={`w-full text-left p-3 rounded-lg border-2 transition ${
                        selectedEnrollment?.id === enrollment.id
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-gray-200 bg-white hover:border-amber-300'
                      }`}
                    >
                      <p className="font-semibold text-gray-800 text-sm">{enrollment.scheme_name}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {enrollment.monthly_amount} {enrollment.currency}/month • {enrollment.duration_months}mo
                      </p>
                      <p className="text-xs text-amber-600 mt-1">
                        Maturity: {new Date(enrollment.maturity_date).toLocaleDateString()}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Column: Payments Form */}
        <div className="lg:col-span-2">
          {selectedEnrollment ? (
            <form onSubmit={submitPayment}>
              {/* Enrollment Summary */}
              <div className="bg-gradient-to-r from-amber-50 to-amber-100 rounded-lg p-4 mb-6 border border-amber-200">
                <p className="text-xs text-gray-600 mb-1">Current Enrollment</p>
                <h3 className="text-lg font-bold text-gray-800">{selectedEnrollment.scheme_name}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-3 text-sm">
                  <div>
                    <p className="text-gray-600">Monthly Amount</p>
                    <p className="font-bold text-amber-600">
                      {selectedEnrollment.currency === 'USD' ? '$' : '₹'}{selectedEnrollment.monthly_amount}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">Status</p>
                    <p className="font-bold text-green-600">Active</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Maturity Date</p>
                    <p className="font-bold">{new Date(selectedEnrollment.maturity_date).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>

              {/* Pending Payments */}
              <div className="bg-white rounded-lg shadow-lg p-6 border border-amber-100">
                <h2 className="font-bold text-gray-800 mb-4 text-lg flex items-center gap-2">
                  <Clock size={20} /> 3. Select Payments to Record
                </h2>

                {paymentsLoading ? (
                  <p className="text-gray-600">Loading pending payments...</p>
                ) : pendingPayments.length === 0 ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-green-700 font-semibold">✓ All payments are up to date!</p>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2 mb-6 max-h-64 overflow-y-auto">
                      {pendingPayments.map((payment, idx) => (
                        <div
                          key={idx}
                          className={`p-4 rounded-lg border-2 cursor-pointer transition ${
                            selectedPayments.includes(idx)
                              ? 'border-green-500 bg-green-50'
                              : 'border-gray-200 bg-white hover:border-gray-300'
                          }`}
                          onClick={() => togglePaymentSelection(idx)}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-6 h-6 rounded border-2 flex items-center justify-center mt-1 transition ${
                              selectedPayments.includes(idx)
                                ? 'border-green-500 bg-green-500'
                                : 'border-gray-300'
                            }`}>
                              {selectedPayments.includes(idx) && (
                                <CheckCircle size={18} className="text-white" />
                              )}
                            </div>
                            <div className="flex-1">
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="font-semibold text-gray-800">
                                    Month {payment.payment_month}
                                  </p>
                                  <p className="text-sm text-gray-600">
                                    Due: {new Date(payment.due_date).toLocaleDateString()}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="font-bold text-amber-600">
                                    {selectedEnrollment.currency === 'USD' ? '$' : '₹'}{parseFloat(payment.payment_amount || 0).toFixed(2)}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {selectedPayments.length > 0 && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
                        <p className="text-blue-800 text-sm">
                          <strong>{selectedPayments.length}</strong> payment(s) selected for recording
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Payment Recording Form */}
              {selectedPayments.length > 0 && pendingPayments.length > 0 && (
                <div className="bg-white rounded-lg shadow-lg p-6 border border-amber-100 mt-6">
                  <h2 className="font-bold text-gray-800 mb-4 text-lg">Payment Details</h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Amount Paid ({selectedEnrollment.currency}) *
                      </label>
                      <input
                        type="number"
                        name="amount_paid"
                        value={paymentForm.amount_paid}
                        onChange={handlePaymentFormChange}
                        step="0.01"
                        placeholder={selectedEnrollment.monthly_amount}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Payment Method
                      </label>
                      <select
                        name="payment_method"
                        value={paymentForm.payment_method}
                        onChange={handlePaymentFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      >
                        <option value="cash">Cash</option>
                        <option value="check">Check</option>
                        <option value="bank_transfer">Bank Transfer</option>
                        <option value="upi">UPI</option>
                        <option value="credit_card">Credit Card</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Payment Date
                      </label>
                      <input
                        type="date"
                        name="payment_date"
                        value={paymentForm.payment_date}
                        onChange={handlePaymentFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Notes (Optional)
                      </label>
                      <input
                        type="text"
                        name="notes"
                        value={paymentForm.notes}
                        onChange={handlePaymentFormChange}
                        placeholder="Add any notes..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full mt-4 bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg font-medium transition flex items-center justify-center gap-2"
                  >
                    <Save size={20} /> {submitting ? 'Recording...' : `Record ${selectedPayments.length} Payment(s)`}
                  </button>
                </div>
              )}
            </form>
          ) : (
            <div className="bg-white rounded-lg shadow-lg p-12 text-center border border-amber-100">
              <Search size={48} className="mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600">Select a customer and enrollment to see pending payments</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SchemePaymentEntry;
